import { inventoryRows, batchQuantity, type InventorySort } from './inventoryView.ts';
import { copiesOnPets } from '@dcc/rules/app/petEquipmentService.ts';
import { useState } from 'react';
import type { AppServices } from '@dcc/rules/app/services.ts';
import type { SheetMutationGate } from '@dcc/rules/app/sheetMutationGate.ts';
import { contentRegistry } from '@dcc/rules/content/dccCoreContent.ts';
import { dccCoreConditions } from '@dcc/rules/content/dccCoreConditions.ts';
import { newId } from '@dcc/rules/app/ids.ts';
import {
  addItem,
  equipOne,
  inventoryLockedByEncounter,
  removeItem,
  setQuantity,
  stowHeld,
  unequipOne,
  type InventoryOutcome,
} from '@dcc/rules/app/inventoryService.ts';
import { useItem } from '@dcc/rules/app/itemUseService.ts';
import { activeEffects } from '@dcc/rules/rules/encounter.ts';
import { inEncounter } from '@dcc/rules/domain/runtime.ts';
import { hotlistAllocation } from '@dcc/rules/rules/hotlist.ts';
import { handUseOf, inHandCopies } from '@dcc/rules/rules/inventory.ts';
import { buildEffectiveCharacter } from '@dcc/rules/app/effectiveCharacter.ts';
import { deriveStatsFor } from '@dcc/rules/app/derivedStats.ts';
import { VitalsHeader } from './VitalsHeader.tsx';
import { usePlaySheet, type PlaySheetController } from './usePlaySheet.ts';
import { inventoryAddChoices, inventoryCatalogue, previewItemUse } from './inventoryHotlist.ts';
import type { CustomItemEditorState } from '@dcc/rules/ui/customItemEditorModel.ts';
import { CustomItemCreator } from './CustomItemCreator.tsx';
import { createCustomItemAndAddOne, type CustomWeaponAttackState } from './customItemFlow.ts';
import { planItemPower } from '@dcc/rules/rules/itemPower.ts';
import { describeExpiration } from '@dcc/rules/rules/encounter.ts';
import type { ItemPowerStatusView, RoomInventory } from '../obr/sheetsApiStorage.ts';
import { describePowerStatus, type ItemPowerUse } from './itemPowerFlow.ts';
import { describePower } from './itemSummary.ts';
import { isRoomItemId } from './roomInventory.ts';

/**
 * What the Inventory needs to offer Item Powers. Supplied by the host because
 * charges live with the table's server, which this panel cannot reach itself.
 */
export interface ItemPowerHost {
  states: Record<string, ItemPowerStatusView>;
  use(itemId: string): Promise<ItemPowerUse>;
  onUsed(inventory: RoomInventory): void | Promise<void>;
}

function refusal(outcome: InventoryOutcome): string | null {
  if (outcome.ok) return null;
  if ('message' in outcome) return outcome.message;
  return outcome.problems.map((problem) => problem.message).join('; ');
}

export function PlayInventoryPanel(props: {
  play: PlaySheetController;
  services: AppServices;
  characterId: string;
  onSaveError: (error: unknown) => Promise<string>;
  coordinationGate: SheetMutationGate;
  showVitals?: boolean;
  itemPowers?: ItemPowerHost;
}) {
  const play = props.play;
  const [configBusy, setConfigBusy] = useState(false);
  const [localNote, setLocalNote] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [addId, setAddId] = useState('');
  const [addCount, setAddCount] = useState('1');
  const [batchCounts, setBatchCounts] = useState<Record<string, string>>({});
  const [scrapCount, setScrapCount] = useState('1');
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<InventorySort>('name');
  const [customProblems, setCustomProblems] = useState<string[]>([]);

  if (play.error && !play.records) return <p className="dcc-save-status dcc-save-warn" role="alert">{play.error}</p>;
  if (!play.records) return <p>Loading…</p>;

  const { character, runtime } = play.records;
  const effects = activeEffects(runtime);
  const effective = buildEffectiveCharacter(character, effects, (id) => contentRegistry.getItem(id));
  const fighting = inEncounter(runtime);
  const configLock = inventoryLockedByEncounter(fighting);
  const disabled = play.busy || configBusy;
  // Keep every catalogue Item available. addItem() deliberately merges another
  // copy into an existing Inventory entry, so hiding owned Items made the add
  // control disappear after the two core Items had been acquired.
  const addable = inventoryAddChoices(character);
  const rows = inventoryRows(character.inventory, id => contentRegistry.getItem(id), filter, search, sort);

  async function runConfig(task: () => Promise<InventoryOutcome>) {
    if (disabled) return;
    const blocked = props.coordinationGate.claim();
    if (blocked) { setLocalError(blocked.message); return; }
    setConfigBusy(true);
    setLocalError(null);
    try {
      const outcome = await task();
      const problem = refusal(outcome);
      if (problem) { setLocalError(problem); return; }
      setLocalNote(outcome.ok ? outcome.lines.join(' ') || 'Saved.' : '');
      play.reload();
    } catch (error) {
      setLocalError(await props.onSaveError(error));
      play.reload();
    } finally {
      props.coordinationGate.release();
      setConfigBusy(false);
    }
  }

  function deps() {
    return {
      characters: props.services.repository,
      catalogue: inventoryCatalogue,
      now: () => new Date().toISOString(),
      runtime,
    };
  }

  async function createCustom(state: CustomItemEditorState, weapon: CustomWeaponAttackState | null, quantity = 1): Promise<boolean> {
    if (disabled || configLock !== null) return false;
    const blocked = props.coordinationGate.claim();
    if (blocked) { setLocalError(blocked.message); return false; }
    setConfigBusy(true);
    setLocalError(null);
    setCustomProblems([]);
    try {
      const result = await createCustomItemAndAddOne(props.services, character, runtime, state, () => new Date().toISOString(), weapon, quantity);
      if (!result.ok) {
        setCustomProblems(result.problems);
        if (result.itemCreated !== null) setLocalError(`Created ${result.itemCreated.name}, but it was not added to Inventory.`);
        play.reload();
        return false;
      }
      setLocalNote(result.note);
      setAddId(result.item.id);
      play.reload();
      return true;
    } catch (error) {
      setLocalError(await props.onSaveError(error));
      play.reload();
      return false;
    } finally {
      props.coordinationGate.release();
      setConfigBusy(false);
    }
  }

  async function usePower(itemId: string) {
    const host = props.itemPowers;
    if (disabled || host === undefined) return;
    const blocked = props.coordinationGate.claim();
    if (blocked) { setLocalError(blocked.message); return; }
    setConfigBusy(true);
    setLocalError(null);
    setLocalNote(null);
    try {
      const result = await host.use(itemId);
      if (!result.ok) { setLocalError(result.reason); return; }
      setLocalNote(result.note);
      await host.onUsed(result.inventory);
    } catch (error) {
      setLocalError(await props.onSaveError(error));
    } finally {
      // The server saved the crawler itself, so this panel's copy is re-read
      // whether the use landed or was refused.
      play.reload();
      props.coordinationGate.release();
      setConfigBusy(false);
    }
  }

  function useConsumable(itemId: string) {
    const preview = previewItemUse(character, runtime, itemId);
    let sicknessApproved = false;
    if (preview.plan.causesPotionSickness) {
      sicknessApproved = window.confirm(
        `Another potion is still inside the 20-second cadence. Using this one will cause Potion Sickness. Continue?`,
      );
      if (!sicknessApproved) return;
    }

    void play.run('The Item could not be used.', async (records) => {
      const live = previewItemUse(records.character, records.runtime, itemId);
      const outcome = useItem(
        records.character,
        records.runtime,
        live.request.itemId,
        live.request.route,
        {
          catalogue: inventoryCatalogue,
          conditions: dccCoreConditions,
          effects: activeEffects(records.runtime),
          newId,
        },
        live.request.route === 'hotlist' ? live.request.slot : undefined,
      );
      if (!outcome.ok) return outcome.reason;
      // If the world changed while the confirmation was open, do not silently
      // accept a NEW Potion Sickness consequence the player did not approve.
      if (outcome.potionSickness && !sicknessApproved && !preview.plan.causesPotionSickness) {
        return 'That potion would now cause Potion Sickness. Nothing was used; press Use again to confirm.';
      }
      return {
        character: outcome.character,
        runtime: outcome.runtime,
        note: outcome.lines.join(' '),
      };
    });
  }

  return (
    <div className="dcc-play">
      {props.showVitals !== false && <VitalsHeader character={character} derived={deriveStatsFor(effective)} />}

      <section className="dcc-creator-card">
        <h2>Inventory</h2>
        {configLock && <p className="dcc-creator-warn">{configLock}</p>}
        <div className="dcc-inventory-filters">
          <label className="dcc-creator-field">Find Item<input value={search} onChange={e => setSearch(e.target.value)} placeholder="Name or description…" /></label>
          <label className="dcc-creator-field">Category<select value={filter} onChange={e => setFilter(e.target.value)}>
            <option value="">All items</option><option value="weapon">Weapons</option><option value="gear">Gear</option><option value="potion">Potions</option><option value="scroll">Scrolls</option><option value="container">Containers</option><option value="misc">Miscellaneous</option><option value="pet">Pet equipment</option><option value="scrap">Scrap</option>
            {['head', 'torso', 'arms', 'legs', 'feet', 'hands', 'accessories'].map(slot => <option key={slot} value={`slot:${slot}`}>{slot} slot</option>)}
          </select></label>
          <label className="dcc-creator-field">Sort<select value={sort} onChange={e => setSort(e.target.value as InventorySort)}><option value="name">Name</option><option value="value-high">Value: high to low</option><option value="value-low">Value: low to high</option><option value="quantity">Quantity: high to low</option></select></label>
        </div>
        <p className="dcc-creator-help">{rows.length} of {character.inventory.length} entries · Value sorts use recorded gold per copy; unknown values appear last.</p>
        {rows.length === 0 ? (
          <p className="dcc-empty">No items match.</p>
        ) : (
          <ul className="dcc-item-list">
            {rows.map((entry) => {
              const item = contentRegistry.getItem(entry.itemId);
              const name = item?.name ?? entry.itemId;
              const hotlisted = hotlistAllocation(character.hotlist, entry.itemId);
              const inHand = inHandCopies(entry, item);
              const isHeldGear = item?.slot === 'hands' && handUseOf(item) === 'held';
              const preview = item?.use ? previewItemUse(character, runtime, entry.itemId) : null;
              const power = item?.power ?? null;
              const powerPlan = power ? planItemPower(item, character, runtime) : null;
              const powerState = props.itemPowers?.states[entry.itemId];
              const powerRefusal = power === null ? null
                : props.itemPowers === undefined ? 'Item Powers need the table connection.'
                  : powerPlan?.refusal ?? powerState?.notReady ?? null;
              const skillLookup = {
                skillName: (id: string) => contentRegistry.getSkill(id)?.name,
                attack: (id: string) => contentRegistry.getAttack(id),
              };
              return (
                <li key={entry.itemId} className="dcc-item-row">
                  <div className="dcc-item-title">
                    <strong>{name}</strong>
                    <span>× {entry.quantity}</span>
                    <span className="dcc-item-meta">{item?.goldValue === undefined ? 'Value unknown' : `${item.goldValue} gold each`}</span>
                  </div>
                  <p className="dcc-item-meta">
                    {entry.equipped > 0 ? `${isHeldGear ? 'Wielded' : 'Equipped'} ${entry.equipped}` : 'Not equipped'}
                    {inHand > 0 ? ` · In hand ${inHand}` : ''}
                    {hotlisted > 0 ? ` · Hotlist ${hotlisted}` : ''}
                    {copiesOnPets(character, entry.itemId) > 0 ? ` · Pet equipment ${copiesOnPets(character, entry.itemId)}` : ''}
                  </p>
                  {item?.description && <p className="dcc-creator-help">{item.description}</p>}
                  {item?.notes && <p className="dcc-creator-help">{item.notes}</p>}
                  {power && (
                    <p className="dcc-item-meta dcc-item-power">
                      {describePower(power, skillLookup)}
                      {describePowerStatus(powerState) ? ` — ${describePowerStatus(powerState)}` : ''}
                    </p>
                  )}
                  {runtime.active.filter((record) => record.effect.sourceId === entry.itemId).map((record) => (
                    <p key={record.effect.id} className="dcc-item-meta dcc-item-buff">
                      Active: {record.effect.label} ({describeExpiration(record)[0] ?? 'until ended'})
                    </p>
                  ))}
                  <div className="dcc-item-actions">
                    {!isRoomItemId(entry.itemId) && <>
                      <label className="dcc-creator-field">Copies to add<input type="number" min={1} max={99} step={1} value={batchCounts[entry.itemId] ?? '1'} disabled={disabled || configLock !== null} onChange={e => setBatchCounts(current => ({ ...current, [entry.itemId]: e.target.value }))} /></label>
                      <button disabled={disabled || configLock !== null || batchQuantity(batchCounts[entry.itemId] ?? '1') === null} onClick={() => { const n = batchQuantity(batchCounts[entry.itemId] ?? '1'); if (n !== null) void runConfig(() => addItem(character, entry.itemId, n, deps())); }}>Add copies</button>
                    </>}
                    {/* Copies of a table Item come from the GM or the Party Inventory, never from a +1. */}
                    <button
                      className="dcc-creator-secondary"
                      disabled={disabled || configLock !== null || isRoomItemId(entry.itemId)}
                      title={isRoomItemId(entry.itemId) ? 'This Item belongs to the table. More copies come from your GM or the Party Inventory.' : undefined}
                      onClick={() => void runConfig(() => setQuantity(character, entry.itemId, entry.quantity + 1, deps()))}
                    >+1</button>
                    <button
                      className="dcc-creator-secondary"
                      disabled={disabled || configLock !== null || entry.quantity <= 1}
                      onClick={() => void runConfig(() => setQuantity(character, entry.itemId, entry.quantity - 1, deps()))}
                    >−1</button>
                    {item?.slot !== null && item !== undefined && (
                      <button
                        className="dcc-creator-secondary"
                        disabled={disabled || configLock !== null}
                        onClick={() => void runConfig(() => equipOne(character, entry.itemId, deps()))}
                      >{isHeldGear ? 'Wield one' : 'Equip one'}</button>
                    )}
                    {entry.equipped > 0 && (
                      <button
                        className="dcc-creator-secondary"
                        disabled={disabled || configLock !== null}
                        onClick={() => void runConfig(() => unequipOne(character, entry.itemId, deps()))}
                      >{isHeldGear ? 'Put away one' : 'Unequip one'}</button>
                    )}
                    {entry.held > 0 && (
                      <button
                        className="dcc-creator-secondary"
                        disabled={disabled || configLock !== null}
                        onClick={() => void runConfig(() => stowHeld(character, entry.itemId, deps()))}
                      >Stow one</button>
                    )}
                    {preview && (
                      <button
                        disabled={disabled || !preview.plan.canUse}
                        title={preview.plan.refusal ?? undefined}
                        onClick={() => useConsumable(entry.itemId)}
                      >{preview.label}</button>
                    )}
                    {power && (
                      <button
                        disabled={disabled || powerRefusal !== null}
                        title={powerRefusal ?? undefined}
                        onClick={() => {
                          if (powerPlan?.causesPotionSickness && !window.confirm(
                            'Another potion is still inside the 20-second cadence. Using this one will cause Potion Sickness. Continue?',
                          )) return;
                          void usePower(entry.itemId);
                        }}
                      >{power.name}</button>
                    )}
                    <button
                      className="dcc-danger"
                      disabled={disabled || configLock !== null}
                      onClick={() => {
                        if (!window.confirm(`Remove every ${name} from this crawler?`)) return;
                        void runConfig(() => removeItem(character, entry.itemId, deps()));
                      }}
                    >Remove all</button>
                  </div>
                  {preview && !preview.plan.canUse && <p className="dcc-creator-help">{preview.plan.refusal}</p>}
                  {power && powerRefusal !== null && <p className="dcc-creator-help">{powerRefusal}</p>}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="dcc-creator-card">
        <h2>Add known Item</h2>
        <p className="dcc-creator-help">Add 1–99 copies at once. Choosing something already owned increases its quantity.</p>
        <label className="dcc-creator-field">
          Item
          <select value={addId} onChange={(event) => setAddId(event.target.value)} disabled={disabled || configLock !== null}>
            <option value="">Choose an Item…</option>
            {addable.map(({ item, ownedQuantity }) => (
              <option key={item.id} value={item.id}>
                {item.name}{ownedQuantity > 0 ? ` (owned ${ownedQuantity})` : ''}
              </option>
            ))}
          </select>
        </label>
        <label className="dcc-creator-field">Copies (1–99)<input type="number" min={1} max={99} step={1} value={addCount} disabled={disabled || configLock !== null} onChange={e => setAddCount(e.target.value)} /></label>
        <div className="dcc-creator-nav">
          <button
            disabled={disabled || configLock !== null || !addId || batchQuantity(addCount) === null}
            onClick={() => {
              const id = addId;
              void runConfig(async () => {
                const count = batchQuantity(addCount);
                if (count === null) throw new Error('Enter a whole number from 1 to 99.');
                const outcome = await addItem(character, id, count, deps());
                if (outcome.ok) setAddId('');
                return outcome;
              });
            }}
          >Add copies</button>
        </div>
      </section>

      <section className="dcc-creator-card"><h2>Scavenged Scrap</h2>
        <p className="dcc-creator-help">Record the amount awarded by your GM after scavenging. Scrap remains an ordinary inventory stack.</p>
        <label className="dcc-creator-field">Scrap to add (1–99)<input type="number" min={1} max={99} step={1} value={scrapCount} disabled={disabled || configLock !== null} onChange={e => setScrapCount(e.target.value)} /></label>
        <button disabled={disabled || configLock !== null || batchQuantity(scrapCount) === null} onClick={() => { const n = batchQuantity(scrapCount); if (n !== null) void runConfig(() => addItem(character, 'scrap', n, deps())); }}>Add Scrap</button>
      </section>
      <CustomItemCreator
        disabled={disabled || configLock !== null}
        problems={customProblems}
        onCreate={createCustom}
      />

      {(localError || play.error) && <p className="dcc-save-status dcc-save-warn" role="alert">{localError ?? play.error}</p>}
      {(localNote || play.note) && <p className="dcc-save-status" role="status">{localNote ?? play.note}</p>}
    </div>
  );
}


export function PlayInventoryTab(props: {
  services: AppServices;
  characterId: string;
  onSaveError: (error: unknown) => Promise<string>;
  coordinationGate: SheetMutationGate;
  itemPowers?: ItemPowerHost;
}) {
  const play = usePlaySheet(props.services, props.characterId, props.onSaveError, props.coordinationGate);
  return <PlayInventoryPanel {...props} play={play} />;
}
